import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Newspaper, Loader2, Printer, Store } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const avisImg = (p) => `${API_BASE}/avis/image/${p}`;
const kr = (n) => (Number(n || 0)).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STORE_LABEL = { falster: "Nykøbing Falster", ringsted: "Ringsted" };
const MONTHS_DA = ["januar","februar","marts","april","maj","juni","juli","august","september","oktober","november","december"];
const fmtRange = (from, to) => {
  if (!from || !to) return "";
  const f = new Date(`${from}T00:00:00`), t = new Date(`${to}T00:00:00`);
  if (isNaN(f.getTime()) || isNaN(t.getTime())) return "";
  if (f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear())
    return `${f.getDate()}.–${t.getDate()}. ${MONTHS_DA[t.getMonth()]} ${t.getFullYear()}`;
  return `${f.getDate()}. ${MONTHS_DA[f.getMonth()]} – ${t.getDate()}. ${MONTHS_DA[t.getMonth()]} ${t.getFullYear()}`;
};

const FLYER_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800;900&family=IBM+Plex+Sans:wght@400;500;600;700&family=Oswald:wght@500;600;700&display=swap');
.avis-root { font-family: 'IBM Plex Sans', system-ui, sans-serif; }
.avis-display { font-family: 'Barlow Condensed', sans-serif; }
.avis-price { font-family: 'Oswald', sans-serif; font-variant-numeric: tabular-nums; }
@media print {
  .avis-noprint { display: none !important; }
  .avis-root { background: #fff !important; }
  .avis-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; gap: 10px !important; }
  .avis-card { break-inside: avoid; box-shadow: none !important; border-color: #ddd !important; }
  .avis-header { background: #C8102E !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

export default function TilbudsAvisPublic() {
  const { slug } = useParams();
  const [avis, setAvis] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/avis/public/${slug}`);
        if (!r.ok) { setState("notfound"); return; }
        setAvis(await r.json());
        setState("ok");
      } catch { setState("notfound"); }
    })();
  }, [slug]);

  if (state === "loading")
    return <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]"><Loader2 className="w-7 h-7 animate-spin text-[#D90429]" /></div>;

  if (state === "notfound")
    return (
      <div className="avis-root min-h-screen flex flex-col items-center justify-center bg-[#F8F9FA] text-center px-6" data-testid="avis-notfound">
        <style>{FLYER_CSS}</style>
        <Newspaper className="w-12 h-12 text-red-200 mb-4" />
        <h1 className="avis-display text-3xl font-extrabold uppercase tracking-tight text-zinc-800">Avisen findes ikke</h1>
        <p className="text-zinc-500 text-sm mt-2">Linket er muligvis udløbet eller ikke offentliggjort endnu.</p>
      </div>
    );

  const items = avis.items || [];
  const rangeStr = fmtRange(avis.valid_from, avis.valid_to);
  const dateStr = avis.updated_at ? new Date(avis.updated_at).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="avis-root min-h-screen bg-[#F8F9FA] pb-20" data-testid="avis-public">
      <style>{FLYER_CSS}</style>

      {/* Header */}
      <header className="avis-header bg-gradient-to-br from-red-700 via-[#C8102E] to-rose-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1.5px, transparent 1.5px)", backgroundSize: "26px 26px" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-9 sm:py-12 relative">
          <div className="flex items-center justify-between gap-4">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full pl-2.5 pr-3.5 py-1.5" data-testid="avis-store-badge">
              <Store className="w-4 h-4" />
              <span className="avis-display font-bold uppercase tracking-wide text-sm">Lagersalg · {STORE_LABEL[avis.store] || avis.store}</span>
            </div>
            <button onClick={() => window.print()} className="avis-noprint inline-flex items-center gap-2 bg-white text-[#C8102E] hover:bg-yellow-300 hover:text-zinc-900 transition-colors font-bold text-sm rounded-full px-4 py-2 shadow-sm" data-testid="avis-print-btn">
              <Printer className="w-4 h-4" /> Skriv ud
            </button>
          </div>
          <div className="text-center mt-5 sm:mt-7">
            <h1 className="avis-display text-5xl sm:text-7xl font-black uppercase tracking-tight leading-[0.9] drop-shadow-sm">Tilbudsavis</h1>
            {avis.name && <p className="avis-display mt-3 inline-block bg-yellow-300 text-zinc-900 font-extrabold uppercase tracking-wide text-lg sm:text-xl px-4 py-1 rounded-md -rotate-1">{avis.name}</p>}
            {avis.week_no && (
              <div className="mt-4 flex items-center justify-center gap-3 flex-wrap" data-testid="avis-week-row">
                <span className="avis-display bg-white text-[#C8102E] font-black uppercase text-lg sm:text-xl px-4 py-1.5 rounded-lg shadow-sm" data-testid="avis-week-badge">Uge {avis.week_no}</span>
                {rangeStr && <span className="avis-display text-white font-bold uppercase tracking-wide text-sm sm:text-base">Gælder {rangeStr}</span>}
              </div>
            )}
            <p className="text-red-100 text-xs sm:text-sm mt-4 uppercase tracking-[0.25em] font-semibold">Gælder så længe lager haves{dateStr ? ` · ${dateStr}` : ""}</p>
          </div>
        </div>
        <div className="h-3 bg-yellow-300" />
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-8 pt-8">
        {items.length === 0 ? (
          <p className="text-center text-zinc-400 py-20">Ingen varer i avisen endnu.</p>
        ) : (
          <div className="avis-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5 items-stretch">
            {items.map((it) => {
              const normal = Number(it.normal_price) || 0;
              const offer = Number(it.offer_price) || 0;
              const hasOffer = offer > 0 && offer < normal;
              const pct = hasOffer && normal ? Math.round((1 - offer / normal) * 100) : 0;
              const save = hasOffer ? normal - offer : 0;
              const shown = hasOffer ? offer : (offer || normal);
              return (
                <div key={it.productno} className="avis-card bg-white rounded-2xl border border-zinc-200/90 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-200 flex flex-col overflow-hidden relative group" data-testid={`avis-pub-item-${it.productno}`}>
                  {/* Image */}
                  <div className="relative w-full aspect-square bg-white overflow-hidden flex items-center justify-center border-b border-zinc-100">
                    {it.image_path
                      ? <img src={avisImg(it.image_path)} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      : <Newspaper className="w-10 h-10 text-zinc-200" />}
                    {pct > 0 && (
                      <div className="absolute top-2.5 left-2.5 flex flex-col items-start gap-1">
                        <span className="avis-display bg-[#FF0035] text-white font-black text-base leading-none px-2.5 py-1.5 rounded-lg shadow-md">-{pct}%</span>
                        {pct >= 40 && <span className="avis-display bg-yellow-300 text-zinc-900 font-extrabold uppercase text-[10px] tracking-wide px-2 py-0.5 rounded">Slagtilbud</span>}
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-3.5 flex flex-col flex-grow">
                    <h3 className="text-sm sm:text-base font-bold text-zinc-900 leading-tight line-clamp-2 min-h-[2.6rem]">{it.title}</h3>
                    <div className="mt-auto pt-3">
                      <div className="flex items-end justify-between gap-2">
                        <div className="flex items-baseline">
                          <span className={`avis-price text-4xl sm:text-5xl font-bold leading-none tracking-tight ${hasOffer ? "text-[#D90429]" : "text-zinc-900"}`}>{kr(shown)}</span>
                          <span className="avis-display text-sm font-bold uppercase ml-1 text-zinc-700">kr</span>
                        </div>
                        {hasOffer && (
                          <div className="text-right leading-tight pb-1">
                            <span className="avis-price text-sm text-zinc-400 line-through font-medium">{kr(normal)}</span>
                          </div>
                        )}
                      </div>
                      {hasOffer && (
                        <span className="avis-display inline-block mt-2 bg-[#06D6A0]/15 text-[#047857] font-extrabold uppercase text-[11px] tracking-wide px-2 py-0.5 rounded">Spar {kr(save)} kr</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <footer className="mt-14 border-t border-zinc-200 pt-6 text-center">
          <p className="avis-display text-lg font-extrabold uppercase tracking-tight text-zinc-800">Lagersalg {STORE_LABEL[avis.store] || avis.store}</p>
          <p className="text-xs text-zinc-500 mt-2 max-w-xl mx-auto">Priserne gælder så længe lager haves. Der tages forbehold for udsolgte varer og trykfejl.</p>
        </footer>
      </main>
    </div>
  );
}
